/**
 * Readiness gate for ClassMap-using components that can mount BEFORE the
 * UI/ClassMap bonds are wired.
 *
 * The App-level ambient banners (HealthBanner, NotificationBanner) mount on
 * every route — including the landing page, whose entry deliberately excludes
 * the ClassMap bonds. Rendering the shared Banner there made `getClassMap()`
 * throw ("No UIClassMap has been set") and took down the whole page via the
 * App ErrorBoundary. This hook is the fix: the component keeps its logic
 * mounted eagerly, and only when it actually has a banner to show does the
 * hook load the UI bonds chunk and report readiness.
 */

import { useEffect, useState } from 'react'

import { hasClassMap } from '@molecule/app-ui'

import { ensureUiProviders } from '../bonds/ui-loader.js'

/**
 * Whether the ClassMap bonds are wired. While `needed` is true and they are
 * not, loads them (once, shared with the route loader) and flips to `true`
 * when done. Callers must render nothing ClassMap-dependent until this
 * returns `true`.
 *
 * @param needed - Whether the caller currently has ClassMap-dependent UI to render.
 * @returns Whether `getClassMap()` is safe to call.
 */
export function useUiProviders(needed: boolean): boolean {
  // Seed from hasClassMap() so environments that wire the ClassMap directly
  // (tests calling setClassMap, routes already loaded) are ready immediately.
  const [ready, setReady] = useState(hasClassMap)

  useEffect(() => {
    if (!needed || ready) return undefined
    // The bonds may have been wired by a route load since the initial render.
    if (hasClassMap()) {
      setReady(true)
      return undefined
    }
    let active = true
    void ensureUiProviders().then(() => {
      if (active) setReady(true)
    })
    return () => {
      active = false
    }
  }, [needed, ready])

  return ready
}
